import { useMemo } from 'react';

import compact from 'lodash/compact';

import {
  Box,
  GridColDef,
  GridRenderCellParams,
  styled,
  Tooltip,
  selectFilterConfig,
  textFilterConfig,
  Icon,
} from '@sbv-clients/ui-shared';
import { nameOfFactory } from '@sbv-clients/util-misc';

import { AdminFacilityEmployeeType } from '../../../services/admin-facility';
import ActionButtons from './actions-buttons';

const employeeRowKey = nameOfFactory<AdminFacilityEmployeeType>();

const actionFilterConfig = () => {
  return {
    sortable: false,
    filterable: false,
    aggregable: false,
    groupable: false,
    pinnable: true,
  };
};

const StyledCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mismatchedProperty',
})<{
  mismatchedProperty?: boolean;
}>(({ theme, mismatchedProperty }) => ({
  color: mismatchedProperty ? theme.palette.error.main : 'inherit',
  display: 'flex',
  alignItems: 'center',
  '& .info-icon': {
    fontSize: '16px',
    cursor: 'pointer',
  },
}));

const useColumns = (facilityId: string) => {
  return useMemo(() => {
    const columns: GridColDef<AdminFacilityEmployeeType>[] = [
      {
        type: 'string',
        field: employeeRowKey('firstName'),
        headerName: 'First Name',
        width: 200,
        ...textFilterConfig(),
      },
      {
        type: 'string',
        field: employeeRowKey('lastName'),
        headerName: 'Last Name',
        width: 200,
        ...textFilterConfig(),
      },
      {
        type: 'string',
        field: employeeRowKey('employeeId'),
        headerName: 'Employee ID',
        width: 200,
        ...textFilterConfig(),
      },
      {
        type: 'string',
        field: employeeRowKey('phone'),
        headerName: 'Legacy Phone',
        width: 200,
        ...textFilterConfig(),
      },
      {
        type: 'string',
        field: employeeRowKey('saasPhone'),
        headerName: 'SaaS Phone',
        width: 200,
        ...textFilterConfig(),
        renderCell: (
          params: GridRenderCellParams<AdminFacilityEmployeeType>
        ) => {
          const tooltipMessage = `Phone in facility database is: ${params.row.phone}`;

          return (
            <StyledCell mismatchedProperty={params.row.isPhoneMismatch}>
              <span>{params.row.saasPhone}</span>
              {params.row.isPhoneMismatch && (
                <Tooltip title={tooltipMessage}>
                  <Icon name={'Info'} className="info-icon" />
                </Tooltip>
              )}
            </StyledCell>
          );
        },
      },
      {
        field: employeeRowKey('role'),
        headerName: 'Facility Role',
        width: 150,
        ...selectFilterConfig(),
        valueOptions: [
          { value: 'Desktop', label: 'Desktop' },
          { value: 'Mobile', label: 'Mobile' },
        ],
      },
      {
        field: employeeRowKey('saasRole'),
        headerName: 'SaaS Role',
        width: 150,
        ...selectFilterConfig(),
        renderCell: (
          params: GridRenderCellParams<AdminFacilityEmployeeType>
        ) => {
          const tooltipMessage = `Role on Legacy is: ${params.row.role}`;

          return (
            <StyledCell mismatchedProperty={params.row.isRoleMismatch}>
              <span>{params.row.saasRole}</span>
              {params.row.isRoleMismatch && (
                <Tooltip title={tooltipMessage}>
                  <Icon name={'Info'} className="info-icon" />
                </Tooltip>
              )}
            </StyledCell>
          );
        },
        valueOptions: [
          { value: 'Desktop', label: 'Desktop' },
          { value: 'Mobile', label: 'Mobile' },
        ],
      },
      {
        type: 'string',
        field: employeeRowKey('saasUsername'),
        headerName: 'SaaS Username',
        minWidth: 280,
        ...textFilterConfig(),
        renderCell: (
          params: GridRenderCellParams<AdminFacilityEmployeeType>
        ) => {
          const tooltipMessage = `Username on Legacy is: ${params.row.facilityLoginId}`;

          return (
            <StyledCell mismatchedProperty={params.row.isUserMismatch}>
              <span>{params.row.saasUsername}</span>
              {params.row.isUserMismatch && (
                <Tooltip title={tooltipMessage}>
                  <Icon name={'Info'} className="info-icon" />
                </Tooltip>
              )}
            </StyledCell>
          );
        },
      },
      {
        type: 'string',
        field: employeeRowKey('otp'),
        headerName: 'Registration OTP',
        width: 200,
        ...textFilterConfig(),
      },
      {
        field: employeeRowKey('isOnboarded'),
        headerName: 'Sent Registration SMS',
        width: 190,
        ...selectFilterConfig(),
      },
      {
        field: employeeRowKey('isRegistered'),
        headerName: 'Registered',
        width: 190,
        ...selectFilterConfig(),
      },
      {
        field: employeeRowKey('isPhoneNumberConfirmed'),
        headerName: 'Phone Number Confirmed',
        width: 190,
        ...selectFilterConfig(),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        ...actionFilterConfig(),
        renderCell: (
          params: GridRenderCellParams<AdminFacilityEmployeeType>
        ) => {
          return (
            <ActionButtons
              facilityId={facilityId}
              fullName={compact([
                params.row.firstName,
                params.row.lastName,
              ]).join(' ')}
              userId={params.row.userId as string}
              isRegistered={params.row.isRegistered ?? false}
              isLocked={params.row.isLocked ?? false}
              instanceEmployeeId={params.row.pkEmployeeId as number}
            />
          );
        },
      },
    ];
    return columns;
  }, [facilityId]);
};

export default useColumns;
